<template>
  <b-modal
    id="modal-bulk-upload-attendance"
    ref="modalBulkUpload"
    title="Bulk Attendance - Upload Excel"
    size="lg"
    @hidden="resetModal"
    @ok="handleUpload">
    <div>
      <!-- Instructions -->
      <div class="alert alert-info mb-4">
        <i class="fe fe-info me-2"></i>
        <strong>Instructions:</strong>
        <ul class="mb-0 mt-2">
          <li>Upload the filled Excel file (.xlsx format only)</li>
          <li>Maximum file size: 5MB</li>
        </ul>
      </div>

      <!-- File Upload -->
      <div class="form-group">
        <label class="form-label">Upload Excel File <span class="text-danger">*</span></label>
        <div class="custom-file">
          <input 
            type="file" 
            class="custom-file-input" 
            id="excelFileAttendance"
            accept=".xlsx"
            @change="handleFileChange"
            ref="fileInput">
          <label class="custom-file-label" for="excelFileAttendance">
            {{ selectedFileName || 'Choose Excel file...' }}
          </label>
        </div>
        <small class="form-text text-muted">
          Accepted format: .xlsx only (max 5MB)
        </small>
      </div>

      <!-- Selected File Info -->
      <div v-if="selectedFile" class="alert alert-success mt-3">
        <i class="fe fe-file-text me-2"></i>
        <strong>Selected file:</strong> {{ selectedFile.name }}
        <br>
        <small>Size: {{ formatFileSize(selectedFile.size) }}</small>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <button 
        type="button" 
        class="btn btn-secondary" 
        @click="cancel()">
        Cancel
      </button>
      <button 
        type="button" 
        class="btn btn-primary"
        :disabled="!selectedFile || isUploading"
        @click="ok()">
        <span v-if="isUploading">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Mengunggah...
        </span>
        <span v-else>
          <i class="fe fe-upload me-2"></i>
          Upload
        </span>
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ModalBulkUploadAttendance',
  props: {
    selectedCourseId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFileName: '',
      isUploading: false
    };
  },
  methods: {
    ...mapActions('mitraAttendance', ['bulkUploadAttendance']),
    
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.name.endsWith('.xlsx')) {
          this.$swal({
            title: 'File Tidak Valid',
            text: 'Pilih file Excel yang valid (.xlsx)',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$swal({
            title: 'File Terlalu Besar',
            text: 'Ukuran file tidak boleh lebih dari 5MB',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },
    
    resetFile() {
      this.selectedFile = null;
      this.selectedFileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    resetModal() {
      this.resetFile();
      this.isUploading = false;
    },
    
    async handleUpload(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault();
      
      if (!this.selectedFile) {
        this.$swal({
          title: 'File Belum Dipilih',
          text: 'Pilih file Excel untuk diunggah',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      if (!this.selectedCourseId) {
        this.$swal({
          title: 'Pelatihan Belum Dipilih',
          text: 'Pilih pelatihan terlebih dahulu',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      this.isUploading = true;

      try {
        // Create FormData
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        // Call API
        const result = await this.bulkUploadAttendance({
          courseId: this.selectedCourseId,
          formData: formData
        });

        // Close upload modal
        this.$refs.modalBulkUpload.hide();

        // Build HTML content for swal
        const htmlContent = this.buildResultHtml(result);

        // Show success message with data mapping
        this.$swal({
          title: 'Success',
          html: htmlContent,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#00c9a7',
          width: '600px'
        });

        // Emit success to refresh data
        this.$emit('upload-success', result);
      } catch (error) {
        const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'File belum berhasil diunggah';
        
        this.$swal({
          title: 'Unggah Gagal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isUploading = false;
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    buildResultHtml(result) {
      const message = result.message || 'Bulk attendance uploaded successfully';
      const data = result.data || {};
      
      let html = `<p style="margin-bottom: 16px;">${message}</p>`;
      
      html += `<div style="text-align: left; background: #f8f9fa; padding: 16px; border-radius: 8px; max-height: 400px; overflow-y: auto;">`;

      // Handle submitted data
      if (data.submitted && data.submitted.length > 0) {
        const totalSubmitted = data.submitted.reduce((sum, item) => sum + (item.data_count || 0), 0);
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 8px; color: #00c9a7;"><strong><i class="fe fe-check-circle"></i> Submitted (${totalSubmitted} records)</strong></p>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #e8f5e9;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Training ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Data Count</th>
                </tr>
              </thead>
              <tbody>
        `;
        data.submitted.forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${item.training_id}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.data_count}</td>
            </tr>
          `;
        });
        html += `</tbody></table></div>`;
      }

      // Handle errors
      if (data.errors && data.errors.length > 0) {
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 8px; color: #e63757;"><strong><i class="fe fe-x-circle"></i> Errors (${data.errors.length} items)</strong></p>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #ffebee;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Training ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Error Message</th>
                </tr>
              </thead>
              <tbody>
        `;
        data.errors.forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${item.training_id}</td>
              <td style="padding: 8px; border: 1px solid #ddd; color: #e63757;">${item.message}</td>
            </tr>
          `;
        });
        html += `</tbody></table></div>`;
      }

      // If no data at all
      if ((!data.submitted || data.submitted.length === 0) && 
          (!data.errors || data.errors.length === 0)) {
        html += `<p style="text-align: center; color: #6c757d;">No data processed</p>`;
      }

      html += `</div>`;

      return html;
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.custom-file-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
